import type { NavItem } from "../components/shell/PortalLayout";
import { LOCAL_WEBSITE_URL } from "./adressen";
import type { PaletteItem } from "../components/shell/CommandPalette";
import { t } from "../lib/sprache";
import { andereBereiche, arbeitsbereiche, bereichName } from "./bereichswahl-model";
import { sichtbareKarten, type Sicht } from "../pages/agentur/einstellungen/baum";

/** Navigation der Agentur an einer Stelle; Rahmen und Einrichtung nutzen dieselbe Liste. */
export const AGENCY_NAV: NavItem[] = [
  { get label() { return t("Übersicht", "Overview"); }, to: "/agentur", icon: "grid", end: true },
  { get label() { return t("Kalender", "Calendar"); }, to: "/agentur/kalender", icon: "calendar" },
  // Revision 5 (Original-Portal): der Bereich heißt in der Navigation „Chat“, wie der runde Chat-Knopf unten rechts
  { label: "Chat", to: "/agentur/nachrichten", icon: "chat" },
  { get label() { return t("Kooperationen", "Deals"); }, to: "/agentur/kooperationen", icon: "users", get group() { return t("Kooperationen", "Deals"); } },
  // Hub (Inhaber 21.10.2026): alle Zusammenhänge Marke, Kampagne, Kooperation, Creator gebündelt (wie der Reiter „Hub“ im Altportal)
  { get label() { return t("Hub", "Hub"); }, to: "/agentur/hub", icon: "hub" },
  // 0155: Content-Ablage (Videos, Bilder, PDF je Kooperation, Monat und Beitrag) direkt nach den Kooperationen
  { label: "Content", to: "/agentur/content", icon: "play" },
  // Insights der Creator (Freigaben an Marken) direkt nach den Kooperationen, wie im Original-Portal
  { label: "Insights", to: "/agentur/creator/insights", icon: "insights" },
  // 0072: Vorstufen vor der Kooperation (Anfrage, Angebot, Verhandlung); zählen nicht als Kooperation
  { get label() { return t("Anfragen", "Inquiries"); }, to: "/agentur/anfragen", icon: "deal" },
  // K-539: Pitch-Listen und Media-Kits sind eine Tariffunktion und brauchen einen eigenen Eintrag (vorher nur über die Creator-Liste)
  { get label() { return t("Pitch-Listen", "Pitch lists"); }, to: "/agentur/creator/pitch", icon: "link" },
  { get label() { return t("Kennzahlen", "Metrics"); }, to: "/agentur/kennzahlen", icon: "chart" },
  { get label() { return t("Verträge", "Contracts"); }, to: "/agentur/vertraege", icon: "file", get group() { return t("Verträge & Geld", "Contracts & money"); } },
  { get label() { return t("Rechnungen", "Invoices"); }, to: "/agentur/rechnungen", icon: "receipt" },
  { label: "Creator", to: "/agentur/creator", icon: "list", get group() { return t("Stammdaten", "Master data"); } },
  { get label() { return t("Marken", "Brands"); }, to: "/agentur/marken", icon: "tag" },
];
export const AGENCY_SETTINGS: NavItem = { get label() { return t("Einstellungen", "Settings"); }, to: "/agentur/einstellungen", icon: "settings", also: ["/agentur/profil"] };
/** Postausgang: alle Mails der Agentur mit Stand und Fehlern (0041), nur für Admin */
export const AGENCY_OUTBOX: NavItem = { get label() { return t("Postausgang", "Outbox"); }, to: "/agentur/postausgang", icon: "arrow" };

/** Hilfe & Support (0176): Tickets an den Betrieb, für das ganze Team */
export const AGENCY_HELP: NavItem = { get label() { return t("Hilfe & Support", "Help & support"); }, to: "/agentur/hilfe", icon: "help" };

/** Auszahlungslauf (0046): nur mit dem Recht „Auszahlungen ausführen“ */
export const AGENCY_PAYOUTS: NavItem = { get label() { return t("Auszahlungen", "Payouts"); }, to: "/agentur/auszahlungen", icon: "wallet" };

/** Kennzahlen nur mit dem Recht „Kennzahlen und Agenturumsatz sehen“ (Admin immer). */
const withFinancials = <T extends { to: string }>(items: T[], financials: boolean): T[] => (financials ? items : items.filter((i) => i.to !== "/agentur/kennzahlen"));
/** Auszahlungen direkt nach den Rechnungen */
const withPayouts = (items: NavItem[], payouts: boolean): NavItem[] => {
  if (!payouts) return items;
  const at = items.findIndex((i) => i.to === "/agentur/rechnungen");
  return at < 0 ? [...items, AGENCY_PAYOUTS] : [...items.slice(0, at + 1), AGENCY_PAYOUTS, ...items.slice(at + 1)];
};

/** Seitenleiste: Postausgang und Einstellungen nur für Admin (die Datenbank prüft ohnehin jede Änderung). */
/** Rechnungen und Auszahlungen nur mit dem Recht „Beträge sehen“ (RECHTE-6) */
const withAmounts = <T extends { to: string }>(items: T[], amounts: boolean): T[] =>
  (amounts ? items : items.filter((i) => !i.to.startsWith("/agentur/rechnungen") && i.to !== "/agentur/auszahlungen" && i.to !== "/agentur/kennzahlen/liquiditaet"));
export function agencyNav(isChef: boolean, financials: boolean = isChef, payouts: boolean = isChef, amounts = true): NavItem[] {
  // Letzte Gruppe des Seitenmenüs: für den Admin „Verwaltung“ ab dem Postausgang, sonst steht nur die Hilfe darunter
  const verwaltung = t("Verwaltung", "Administration");
  const ende = isChef ? [{ ...AGENCY_OUTBOX, group: verwaltung }, AGENCY_SETTINGS, AGENCY_HELP] : [{ ...AGENCY_HELP, group: t("Hilfe", "Help") }];
  return withAmounts(withFinancials(withPayouts([...AGENCY_NAV, ...ende], payouts), financials), amounts || isChef);
}
/**
 * Creator-Konto (0178): dieselben Seiten wie die Agentur, aber nur, was eine selbständige Person braucht. Kein Chat (es gibt
 * kein Gegenüber im Konto), keine Creator-Liste (an ihrer Stelle das eigene Profil), keine Auszahlungen; Postausgang,
 * Einstellungen und Hilfe wie bei der Agentur.
 */
export const STUDIO_PROFILE: NavItem = { get label() { return t("Mein Profil", "My profile"); }, to: "/agentur/ich", icon: "user", also: ["/agentur/creator"] };
const STUDIO_WEG = new Set(["/agentur/nachrichten", "/agentur/creator", "/agentur/creator/insights", "/agentur/creator/pitch", "/agentur/kennzahlen"]);
export function studioNav(): NavItem[] {
  const main = AGENCY_NAV.filter((i) => !STUDIO_WEG.has(i.to)).map((i) => (i.to === "/agentur/kooperationen" ? { ...i, label: "Deals", group: "Deals" } : i));
  const at = main.findIndex((i) => i.to === "/agentur/marken");
  return [...main.slice(0, at), { ...STUDIO_PROFILE, group: t("Stammdaten", "Master data") }, ...main.slice(at), { ...AGENCY_OUTBOX, group: t("Verwaltung", "Administration") }, AGENCY_SETTINGS, AGENCY_HELP];
}
/**
 * Seiten, die es im Studio (Creator-Konto) nicht gibt, und wohin ihr Weg stattdessen führt (Studio-Texte 22.10.2026): kein Team,
 * keine Rollen, kein Chat, keine Creator-Liste, keine Auszahlungen, keine Kennzahlen der Agentur, kein WhatsApp an Creator. Das
 * eigene Profil (/agentur/creator/<id>) und sein Media-Kit bleiben. Nicht freigeschaltete Seiten von „Alle Einstellungen“
 * (Provision, Gutschriften, Rollen …) leiten die Seiten selbst über baum.ts um.
 */
const STUDIO_UMLEITUNGEN: readonly [RegExp, string][] = [
  [/^\/agentur\/creator\/?$/, "/agentur/ich"],
  [/^\/agentur\/creator\/(einladen|import|pitch|insights)(\/|$)/, "/agentur/ich"],
  [/^\/agentur\/creator\/[^/]+\/insights(\/|$)/, "/agentur/ich"],
  [/^\/agentur\/nachrichten(\/|$)/, "/agentur"],
  // Die Liquiditätsvorschau (Rechnungen) bleibt; die Kennzahlen der Agentur (Provision, Agenturumsatz) nicht
  [/^\/agentur\/kennzahlen\/?$/, "/agentur"],
  [/^\/agentur\/auszahlungen(\/|$)/, "/agentur/rechnungen"],
  // Support-Zugänge (sonst unter Team · Sicherheit) stehen im Studio unter Anmeldung & Sicherheit
  [/^\/agentur\/einstellungen\/team\/?$/, "/agentur/einstellungen/sicherheit"],
  [/^\/agentur\/einstellungen\/(rollen|whatsapp|provision|abrechnungsweg|staffel|herkunft-nachprovision|gutschriften|creator-sicht|module)\/?$/, "/agentur/einstellungen"],
];
/** Ziel der Umleitung im Studio, null, wenn es die Seite dort gibt */
export function studioUmleitung(pathname: string): string | null {
  return STUDIO_UMLEITUNGEN.find(([re]) => re.test(pathname))?.[1] ?? null;
}
/** Handy: Übersicht, Deals, Rechnungen und „Mehr“ */
export function studioBottom(): NavItem[] {
  const all = studioNav();
  const pick = (to: string) => all.find((i) => i.to === to)!;
  const main = [pick("/agentur"), pick("/agentur/kooperationen"), pick("/agentur/rechnungen")];
  const rest = all.filter((i) => !main.includes(i)).map((i) => i.to);
  return [...main, { get label() { return t("Mehr", "More"); }, to: AGENCY_MORE_PATH, icon: "menu", also: [...rest, "/agentur/benachrichtigungen", "/agentur/profil", "/agentur/creator"] }];
}
export function studioMore(): NavItem[] {
  const unten = new Set(["/agentur", "/agentur/kooperationen", "/agentur/rechnungen"]);
  return studioNav().filter((i) => !unten.has(i.to));
}
/** IA-1: Karten von „Alle Einstellungen“ als Ziele der Befehlspalette (Titel · Gruppe, Synonyme als Suchhinweis). */
export function einstellungenPalette(sicht: Sicht): PaletteItem[] {
  return sichtbareKarten(sicht).map((k) => ({ label: `${t("Einstellungen", "Settings")} · ${k.titel()}`, to: k.to, hint: [...(k.synonyme?.() ?? []), ...(k.neutral ?? [])].join(" ") }));
}

export function studioPalette(): PaletteItem[] {
  const weg = new Set(["/agentur/nachrichten", "/agentur/creator", "/agentur/creator/einladen", "/agentur/creator/insights", "/agentur/kennzahlen"]);
  return [
    ...AGENCY_PALETTE.filter((i) => !weg.has(i.to)).map((i) => (i.to === "/agentur/kooperationen" ? { ...i, label: "Deals" } : i.to === "/agentur/kooperationen/neu" ? { ...i, label: t("Deal anlegen", "Create deal") } : i)),
    { label: t("Mein Profil", "My profile"), to: "/agentur/ich" },
    { label: t("Postausgang", "Outbox"), to: "/agentur/postausgang" },
    { label: t("Alle Einstellungen", "All settings"), to: "/agentur/einstellungen" },
    ...einstellungenPalette({ chef: true, creator: true }),
  ];
}

/** Weg der Seite „Mehr“ (Handy): alle Bereiche, die in der unteren Leiste keinen Platz haben (SICHT-AGENTUR-GELD-9). */
export const AGENCY_MORE_PATH = "/agentur/mehr";

/**
 * Leiste unten auf dem Handy: Übersicht, Kooperationen, Chat und „Mehr“ mit allen übrigen Bereichen der
 * Seitenleiste (Rechnungen, Auszahlungen, Anfragen, Insights, Kennzahlen, Verträge, Creator, Marken, Kalender, Postausgang,
 * Einstellungen), gefiltert mit denselben Rechten wie die Seitenleiste. „Mehr“ gilt als aktiv, solange einer dieser
 * Bereiche offen ist.
 */
export function agencyBottom(isChef: boolean, financials: boolean = isChef, payouts: boolean = isChef, amounts = true): NavItem[] {
  const main = [AGENCY_NAV[0]!, AGENCY_NAV[3]!, AGENCY_NAV[2]!];
  const rest = agencyMore(isChef, financials, payouts, amounts).map((i) => i.to);
  return [...main, { get label() { return t("Mehr", "More"); }, to: AGENCY_MORE_PATH, icon: "menu", also: [...rest, "/agentur/benachrichtigungen", "/agentur/profil"] }];
}

/**
 * Ziele der Seite „Mehr“: die Seitenleiste ohne die Ziele der unteren Leiste. Prüf-Chat R-14 (Bedienbarkeits-Prüfung, Inhaber
 * 02.11.2026): Für „Rechnungen“ ist in der Leiste kein Platz („Kooperationen“ braucht die Breite), deshalb steht sie in „Mehr“
 * an erster Stelle.
 */
export function agencyMore(isChef: boolean, financials: boolean = isChef, payouts: boolean = isChef, amounts = true): NavItem[] {
  const unten = new Set([AGENCY_NAV[0]!.to, AGENCY_NAV[3]!.to, AGENCY_NAV[2]!.to]);
  const liste = agencyNav(isChef, financials, payouts, amounts).filter((i) => !unten.has(i.to));
  const zuerst = "/agentur/rechnungen";
  return [...liste.filter((i) => i.to === zuerst), ...liste.filter((i) => i.to !== zuerst)];
}

export const AGENCY_PALETTE: PaletteItem[] = [
  { get label() { return t("Übersicht", "Overview"); }, to: "/agentur" },
  { get label() { return t("Kooperationen", "Deals"); }, to: "/agentur/kooperationen" },
  { get label() { return t("Hub", "Hub"); }, to: "/agentur/hub", get hint() { return t("Zusammenhänge Marke Kampagne Creator Stand", "connections brand campaign creator status"); } },
  { label: "Content", to: "/agentur/content" },
  { get label() { return t("Anfragen und Angebote", "Inquiries and offers"); }, to: "/agentur/anfragen" },
  { get label() { return t("Verträge", "Contracts"); }, to: "/agentur/vertraege" },
  { get label() { return t("Rechnungen", "Invoices"); }, to: "/agentur/rechnungen" },
  { label: "Creator", to: "/agentur/creator" },
  { get label() { return t("Marken", "Brands"); }, to: "/agentur/marken" },
  { get label() { return t("Kalender", "Calendar"); }, to: "/agentur/kalender" },
  { label: "Chat", to: "/agentur/nachrichten" },
  { label: "Insights", to: "/agentur/creator/insights" },
  { get label() { return t("Kennzahlen", "Metrics"); }, to: "/agentur/kennzahlen" },
  { get label() { return t("Benachrichtigungen", "Notifications"); }, to: "/agentur/benachrichtigungen" },
  { get label() { return t("Hilfe & Support", "Help & support"); }, to: "/agentur/hilfe" },
  { get label() { return t("Kooperation anlegen", "Create deal"); }, to: "/agentur/kooperationen/neu", hint: "neu" },
  { get label() { return t("Anfrage anlegen", "Create inquiry"); }, to: "/agentur/anfragen/neu", hint: "neu" },
  { get label() { return t("Vertrag erstellen", "Create contract"); }, to: "/agentur/vertraege/neu", hint: "neu" },
  { get label() { return t("Rechnung erstellen", "Create invoice"); }, to: "/agentur/rechnungen/neu", hint: "neu" },
  { get label() { return t("Creator anlegen", "Add creator"); }, to: "/agentur/creator/einladen", hint: "neu" },
];
export function agencyPalette(isChef: boolean, financials: boolean = isChef, payouts: boolean = isChef, amounts = true): PaletteItem[] {
  const base = [...AGENCY_PALETTE, ...(payouts ? [{ label: t("Auszahlungen", "Payouts"), to: "/agentur/auszahlungen" }] : [])];
  // Unterseiten der Einstellungen einzeln findbar (K-121); seit IA-1 aus dem Einstellungsbaum (Karten, Synonyme als Hinweis)
  const einstellungen: PaletteItem[] = [
    { label: t("Alle Einstellungen", "All settings"), to: "/agentur/einstellungen" },
    ...einstellungenPalette({ chef: true }),
  ];
  return withAmounts(withFinancials(isChef ? [...base, { label: t("Postausgang", "Outbox"), to: "/agentur/postausgang" }, ...einstellungen] : base, financials), amounts || isChef);
}

// ── Creator-Portal ───────────────────────────────────────────────────────────

type CreatorLang = "de" | "en";
const L = (lang: CreatorLang, de: string, en: string) => (lang === "en" ? en : de);

/** Konto und Sicherheit (Abmelden, Passwort, Anmeldeadresse, Datenauskunft, Löschung): an allen Größen und im Archiv (Befunde 3 und 4). */
const creatorAccount = (lang: CreatorLang): NavItem => ({ label: L(lang, "Konto", "Account"), to: "/creator/konto", icon: "settings" });

/** Seitenleiste des Creator-Portals; archivierte Profile sehen nur ihre Belege (0045) und ihr Konto. */
export function creatorNav(lang: CreatorLang = "de", opts: { archived?: boolean; instagram?: boolean; gesperrt?: boolean } = {}): NavItem[] {
  // 0425: Vertrag der Agentur beendet: nur noch Konto (eigener Daten-Download)
  if (opts.gesperrt) return [creatorAccount(lang)];
  if (opts.archived) {
    return [
      { label: L(lang, "Rechnungen", "Invoices"), to: "/creator/rechnungen", icon: "receipt" },
      { label: L(lang, "Verträge", "Contracts"), to: "/creator/vertraege", icon: "file" },
      creatorAccount(lang),
    ];
  }
  return [
    { label: L(lang, "Übersicht", "Overview"), to: "/creator", icon: "grid", end: true },
    { label: L(lang, "Kalender", "Calendar"), to: "/creator/kalender", icon: "calendar" },
    { label: L(lang, "Nachrichten", "Messages"), to: "/creator/nachrichten", icon: "chat" },
    // Paket 2: Zeichen wie in der Agentur-Navigation bzw. im Original (Kooperationen, Rechnung, Profil); Beschriftungen bleiben
    { label: L(lang, "Deine Deals", "Your deals"), to: "/creator/deals", icon: "users" },
    // Hub (21.10.2026): eigene Deals je Marke mit dem Stand von Vertrag, Content und Rechnung
    { label: L(lang, "Meine Marken", "My brands"), to: "/creator/marken", icon: "hub" },
    { label: L(lang, "Rechnungen", "Invoices"), to: "/creator/rechnungen", icon: "receipt" },
    { label: L(lang, "Verträge", "Contracts"), to: "/creator/vertraege", icon: "file" },
    { label: L(lang, "Dein Profil", "Your profile"), to: "/creator/profil", icon: "user", also: opts.instagram ? ["/creator/instagram"] : [] },
    creatorAccount(lang),
  ];
}

/**
 * Leiste unten auf dem Handy (Befund 5): Übersicht, Deals, Rechnungen, Nachrichten und „Mehr“ mit allen übrigen Zielen
 * (Kalender, Verträge, Profil, Benachrichtigungen …). Archiviert: Rechnungen, Verträge.
 */
export function creatorBottom(lang: CreatorLang = "de", opts: { archived?: boolean; gesperrt?: boolean } = {}): NavItem[] {
  if (opts.archived || opts.gesperrt) return creatorNav(lang, opts);
  const nav = creatorNav(lang);
  const pick = (to: string) => nav.find((n) => n.to === to)!;
  const [overview, messages, deals, invoices] = [pick("/creator"), pick("/creator/nachrichten"), pick("/creator/deals"), pick("/creator/rechnungen")];
  // Kurze Beschriftung, damit fünf Ziele in 375 px passen
  return [overview!, { ...deals!, label: "Deals" }, invoices!, messages!, { label: L(lang, "Mehr", "More"), to: "/creator/mehr", icon: "menu", also: ["/creator/kalender", "/creator/marken", "/creator/vertraege", "/creator/profil", "/creator/instagram", "/creator/benachrichtigungen", "/creator/konto"] }];
}

export function creatorPalette(lang: CreatorLang = "de", opts: { archived?: boolean; instagram?: boolean; agency?: boolean; gesperrt?: boolean } = {}): PaletteItem[] {
  const signOut: PaletteItem = { label: L(lang, "Abmelden", "Sign out"), to: "/creator/abmelden" };
  if (opts.archived || opts.gesperrt) return [...creatorNav(lang, opts).map((n) => ({ label: n.label, to: n.to })), signOut];
  return [
    { label: L(lang, "Übersicht", "Overview"), to: "/creator" },
    { label: L(lang, "Deine Deals", "Your deals"), to: "/creator/deals" },
    { label: L(lang, "Meine Marken", "My brands"), to: "/creator/marken", hint: L(lang, "Hub Vertrag Content Rechnung", "hub contract content invoice") },
    { label: L(lang, "Rechnungen", "Invoices"), to: "/creator/rechnungen" },
    { label: L(lang, "Rechnung einreichen", "Submit invoice"), to: "/creator/rechnungen/einreichen", hint: L(lang, "neu", "new") },
    { label: L(lang, "Verträge", "Contracts"), to: "/creator/vertraege" },
    { label: L(lang, "Nachrichten", "Messages"), to: "/creator/nachrichten" },
    { label: L(lang, "Benachrichtigungen", "Notifications"), to: "/creator/benachrichtigungen" },
    { label: L(lang, "Kalender", "Calendar"), to: "/creator/kalender" },
    { label: L(lang, "Dein Profil", "Your profile"), to: "/creator/profil" },
    ...(opts.instagram ? [{ label: "Instagram", to: "/creator/instagram" }] : []),
    ...(opts.agency ? [{ label: L(lang, "Zur Agentur", "Agency workspace"), to: "/agentur" }] : []),
    { label: L(lang, "Konto und Sicherheit", "Account and security"), to: "/creator/konto" },
    signOut,
  ];
}

/** Bereichswechsel für Personen mit Doppelrolle (Team und Creator, Befund 16). */
export const CREATOR_AREA_LINK: NavItem = { get label() { return t("Mein Creator-Portal", "My creator portal"); }, to: "/creator", icon: "users" };

type BereichsKonto = Parameters<typeof arbeitsbereiche>[0];
/**
 * Bereichswechsel im Team-Bereich (Navigation, „Mehr“, Befehlspalette). S2 (0540): Im eigenen Creator-Konto heißen die Ziele
 * „Bei <Agentur>“ und führen in das Creator-Portal genau dieser Agentur; sonst wie bisher „Mein Creator-Portal“.
 */
export function teamBereichsWechsel(a: BereichsKonto, ws: { isCreator?: boolean; org: { org_id: string } }): NavItem[] {
  if (ws.isCreator) {
    return andereBereiche(a, { art: "eigen", orgId: ws.org.org_id }).filter((b) => b.art === "agentur")
      .map((b) => ({ label: bereichName(b, t), to: b.to, icon: "users" as const }));
  }
  return a.talentLinks.length ? [CREATOR_AREA_LINK] : [];
}
/** Bereichswechsel im Creator-Portal: „Mein Konto“ zum eigenen Creator-Konto (S2), „Zur Agentur“ für Teammitglieder */
export function creatorBereichsWechsel(a: BereichsKonto, lang: CreatorLang = "de"): NavItem[] {
  const eigen = arbeitsbereiche(a).filter((b) => b.art === "eigen")
    .map((b) => ({ label: L(lang, "Mein Konto", "My account"), to: b.to, icon: "user" as const }));
  const team = a.memberships.find((m) => a.orgs[m.org_id]?.kind !== "creator");
  return [...eigen, ...(team ? [{ label: L(lang, "Zur Agentur", "Agency workspace"), to: eigen.length ? `/agentur?bereich=${team.org_id}` : "/agentur", icon: "users" as const }] : [])];
}
// Kein Weg mehr vom Kundenportal in die Betreiber-Konsole (Inhaber 03.11.2026): die hat ihre eigene Anmeldung unter /intern/anmelden.

/**
 * Öffentliche Website (AGB, Datenschutz, Preise); lokal der Vorschau-Server der Website. In Produktion aus VITE_WEBSITE_URL;
 * der Build bricht ab, wenn sie fehlt oder auf localhost zeigt (src/app/adressen.ts, vite.config.ts).
 */
export const WEBSITE_URL = ((import.meta.env.VITE_WEBSITE_URL as string | undefined)?.trim() || LOCAL_WEBSITE_URL).replace(/\/+$/, "");
/** Ausführliche Anleitung (Video 2, 14 Min., Fassung 04.11.2026): Seite der Website mit Kapitelmarken, verlinkt aus „Alle Einstellungen“ und „Hilfe & Support“ */
export const ANLEITUNG_VIDEO_URL = `${WEBSITE_URL}/anleitung-video`;
